/**
 * Trunnion AI - Notification Center Component
 * Slide-out panel anchored to the bell icon, with tabs for all notifications and approvals.
 */

import { useState, useEffect, useCallback } from 'react';
import { X, Bell } from 'lucide-react';
import { useNotificationStore } from '@/stores/notificationStore';
import { NotificationList } from './NotificationList';
import { ApprovalQueuePanel } from './ApprovalQueuePanel';

type Tab = 'all' | 'approvals';

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
}

export function NotificationCenter({ open, onClose }: NotificationCenterProps) {
  const [activeTab, setActiveTab] = useState<Tab>('all');
  const [visible, setVisible] = useState(false);
  const {
    approvalQueue,
    unreadCount,
    markAllRead,
    clearHistory,
    clearApprovalQueue,
  } = useNotificationStore();

  // Animate in/out
  useEffect(() => {
    if (open) {
      // Small delay to trigger CSS transition after mount
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true);
        });
      });
    } else {
      setVisible(false);
    }
  }, [open]);

  const handleOverlayClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  const handleClearAll = () => {
    if (activeTab === 'all') {
      clearHistory();
    } else {
      clearApprovalQueue();
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-90 transition-colors duration-200 ${
          visible ? 'bg-black/30' : 'bg-transparent'
        }`}
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={`fixed right-0 top-14 bottom-0 w-96 z-95 flex flex-col bg-trunnion-slate-900 border-l border-trunnion-slate-700 shadow-2xl transition-transform duration-300 ease-out ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-label="Notification Center"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-trunnion-slate-700 px-4 py-3">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-trunnion-gold-500" />
            <h2 className="text-base font-semibold text-white">Notifications</h2>
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center rounded-full bg-trunnion-gold-500/10 px-2 py-0.5 text-xs font-medium text-trunnion-gold-500">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-trunnion-slate-400 hover:text-trunnion-gold-500 transition-colors"
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded p-1 text-trunnion-slate-400 hover:text-white hover:bg-trunnion-slate-800 transition-colors"
              aria-label="Close notification center"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-trunnion-slate-700">
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'all'
                ? 'border-b-2 border-trunnion-gold-500 text-trunnion-gold-500'
                : 'text-trunnion-slate-400 hover:text-trunnion-slate-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab('approvals')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
              activeTab === 'approvals'
                ? 'border-b-2 border-trunnion-gold-500 text-trunnion-gold-500'
                : 'text-trunnion-slate-400 hover:text-trunnion-slate-200'
            }`}
          >
            Approvals
            {approvalQueue.length > 0 && (
              <span
                className={`inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                  activeTab === 'approvals'
                    ? 'bg-trunnion-gold-500/20 text-trunnion-gold-500'
                    : 'bg-status-waiting-approval/10 text-status-waiting-approval'
                }`}
              >
                {approvalQueue.length}
              </span>
            )}
          </button>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'all' ? <NotificationList /> : <ApprovalQueuePanel />}
        </div>

        {/* Footer */}
        <div className="border-t border-trunnion-slate-700 px-4 py-2.5">
          <button
            onClick={handleClearAll}
            className="w-full rounded-md py-1.5 text-xs font-medium text-trunnion-slate-400 hover:text-red-400 hover:bg-trunnion-slate-800 transition-colors"
          >
            {activeTab === 'all' ? 'Clear all notifications' : 'Clear all approvals'}
          </button>
        </div>
      </div>
    </>
  );
}
